import { useState, useEffect, useRef, useId, useReducer } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, ArrowRight, Loader2, Home, Building } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { hasHouseNumber, sanitizeInput } from "@/lib/addressValidation";
import { FunnelEvent, trackFunnel } from "@/lib/funnel";
import { interpolate, useLocale } from "@/i18n/context";
import { cn } from "@/lib/utils";
import { interpretComboboxKey, listboxReducer, type ListboxState } from "@/lib/listboxKeys";

interface HeroAddressSearchProps {
  onAddressSelected: (address: string, shouldScroll?: boolean, propertyType?: string, apartmentNumber?: string) => void;
  loading?: boolean;
  variant?: "hero" | "dashboard";
  submitLabel?: string;
  /** Prefills the address field, e.g. from a `?address=` link in an email. */
  initialAddress?: string;
  /** Prefills the house/apartment choice and the apartment number alongside it. */
  initialPropertyType?: "hisa" | "stanovanje";
  initialApartmentNumber?: string;
  /**
   * Bumping this clears the address, apartment number and suggestions — the
   * dashboard does it after an order is placed so the same address is not sat
   * in the form, one click away from a second credit.
   */
  resetKey?: number;
}

interface AutocompleteResult {
  place_id: string;
  description: string;
  main_text: string;
  secondary_text: string;
}

const CLOSED: ListboxState = { open: false, highlighted: -1 };

export function HeroAddressSearch({
  onAddressSelected,
  loading: externalLoading,
  variant = "hero",
  submitLabel,
  resetKey,
  initialAddress,
  initialPropertyType,
  initialApartmentNumber,
}: HeroAddressSearchProps) {
  const { t } = useLocale();
  const [address, setAddress] = useState(() => sanitizeInput(initialAddress ?? ""));
  const [propertyType, setPropertyType] = useState<"hisa" | "stanovanje">(initialPropertyType ?? "hisa");
  const [apartmentNumber, setApartmentNumber] = useState(() =>
    initialPropertyType === "stanovanje" ? sanitizeInput(initialApartmentNumber ?? "").slice(0, 20) : "",
  );
  const [loading, setLoading] = useState(false);
  const [addressError, setAddressError] = useState("");
  const [apartmentError, setApartmentError] = useState("");
  const [autocompleteResults, setAutocompleteResults] = useState<AutocompleteResult[]>([]);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const apartmentRef = useRef<HTMLInputElement>(null);
  const requestControllerRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteCacheRef = useRef<Map<string, AutocompleteResult[]>>(new Map());
  const latestRequestIdRef = useRef(0);
  const inFlightKeyRef = useRef<string | null>(null);

  // Combobox state (WAI-ARIA list autocomplete, manual selection): which
  // suggestion the arrow keys have reached and whether the list is showing.
  const resultCount = autocompleteResults.length;
  const [listbox, dispatch] = useReducer(
    (state: ListboxState, action: Parameters<typeof listboxReducer>[1]) =>
      listboxReducer(state, action, resultCount),
    CLOSED,
  );
  const listId = useId();
  const addressErrorId = `${listId}-address-error`;
  const apartmentErrorId = `${listId}-apartment-error`;
  const optionId = (index: number) => `${listId}-option-${index}`;
  const listOpen = listbox.open && resultCount > 0;

  const showResults = (results: AutocompleteResult[]) => {
    setAutocompleteResults(results);
    dispatch({ type: "results", count: results.length });
  };

  const searchPlaces = async (input: string) => {
    const key = input.trim().toLowerCase();
    if (!key || key.length < 3) {
      showResults([]);
      return;
    }

    // Cache hit: instantaneous.
    const cached = autocompleteCacheRef.current.get(key);
    if (cached) {
      showResults(cached);
      return;
    }

    // Dedupe identical in-flight requests.
    if (inFlightKeyRef.current === key) return;

    const requestId = ++latestRequestIdRef.current;
    inFlightKeyRef.current = key;
    const controller = new AbortController();
    requestControllerRef.current = controller;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('places-autocomplete', {
        body: { input: input.trim() },
        signal: controller.signal
      });

      if (error) throw error;

      // Ignore stale results (user typed something else since this request started).
      if (requestId !== latestRequestIdRef.current) return;

      const results = (data?.predictions || []) as AutocompleteResult[];
      // Keep a small per-form cache; no storage or extra network layer.
      if (autocompleteCacheRef.current.size >= 30) {
        autocompleteCacheRef.current.delete(autocompleteCacheRef.current.keys().next().value!);
      }
      autocompleteCacheRef.current.set(key, results);
      showResults(results);
    } catch (error) {
      if (requestId !== latestRequestIdRef.current) return;
      console.error('Places autocomplete error:', error);
      showResults([]);
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setLoading(false);
        requestControllerRef.current = null;
        if (inFlightKeyRef.current === key) inFlightKeyRef.current = null;
      }
    }
  };

  const cancelPendingSearch = () => {
    latestRequestIdRef.current += 1;
    inFlightKeyRef.current = null;
    requestControllerRef.current?.abort();
    requestControllerRef.current = null;
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = sanitizeInput(e.target.value);
    setAddress(value);
    setAddressError("");
    const key = value.trim().toLowerCase();
    // Case and whitespace edits do not need another identical lookup.
    if (key === address.trim().toLowerCase()) return;

    // Invalidate at the edit, including the debounce window and cache hits.
    cancelPendingSearch();
    showResults([]);
    setLoading(false);
    if (key.length < 3) return;
    const cached = autocompleteCacheRef.current.get(key);
    if (cached) {
      showResults(cached);
      return;
    }
    debounceTimeoutRef.current = setTimeout(() => searchPlaces(value), 300);
  };

  const selectAddress = (result: AutocompleteResult) => {
    // Invalidate in-flight request; user has made a selection.
    cancelPendingSearch();
    setAddress(result.description);
    setAddressError("");
    setLoading(false);
    showResults([]);
    // Keep focus in the input: the keyboard user is about to add an apartment
    // number or press Enter to submit. (The old blur dropped them on the page.)
    inputRef.current?.focus();
    // Don't auto-trigger onAddressSelected - wait for user to submit
  };

  const handleQuickSearch = () => {
    const invalidAddress = !address.trim() ? t.addressSearch.toastEnterAddressDesc
      : !hasHouseNumber(address) || !/\p{L}/u.test(address) ? t.addressSearch.incompleteAddress : "";
    const invalidApartment = propertyType === "stanovanje" && !apartmentNumber.trim()
      ? t.addressSearch.toastEnterAptDesc : "";
    setAddressError(invalidAddress);
    setApartmentError(invalidApartment);
    if (invalidAddress || invalidApartment) {
      (invalidAddress ? inputRef : apartmentRef).current?.focus();
      return;
    }
    cancelPendingSearch();
    showResults([]);
    setLoading(false);

    trackFunnel(FunnelEvent.AddressSubmitted, {
      propertyType,
      hasAddress: true,
      source: variant,
    });

    onAddressSelected(address.trim(), true, propertyType, propertyType === "stanovanje" ? apartmentNumber.trim() : undefined);
  };

  const dismissSuggestions = () => {
    cancelPendingSearch();
    setLoading(false);
    dispatch({ type: "close" });
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Escape") dismissSuggestions();
    const action = interpretComboboxKey(e.key, listbox, resultCount);
    if (!action) return;
    switch (action.kind) {
      case "move":
        e.preventDefault();
        dispatch({ type: "move", by: action.by });
        break;
      case "select":
        e.preventDefault();
        selectAddress(autocompleteResults[action.index]);
        break;
      case "submit":
        e.preventDefault();
        if (!externalLoading) handleQuickSearch();
        break;
      case "close":
        if (action.preventDefault) e.preventDefault();
        dispatch({ type: "close" });
        break;
    }
  };

  const handleApartmentKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing && !externalLoading) {
      e.preventDefault();
      handleQuickSearch();
    }
  };
  
  const handlePropertyTypeChange = (type: "hisa" | "stanovanje") => {
    setPropertyType(type);
    setApartmentError("");
    if (type === "hisa") {
      setApartmentNumber("");
    }
  };

  useEffect(() => () => cancelPendingSearch(), []);

  // Clicking or tapping anywhere outside the search closes the suggestions.
  useEffect(() => {
    if (!listOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        dispatch({ type: "close" });
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [listOpen]);

  // Keep the highlighted option scrolled into view inside the list.
  useEffect(() => {
    if (!listOpen || listbox.highlighted < 0) return;
    document
      .getElementById(`${listId}-option-${listbox.highlighted}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [listOpen, listbox.highlighted, listId]);

  // Parent-driven reset (after a successful order).
  const lastResetKey = useRef(resetKey);
  useEffect(() => {
    if (resetKey === undefined || resetKey === lastResetKey.current) return;
    lastResetKey.current = resetKey;
    cancelPendingSearch();
    setAddress("");
    setApartmentNumber("");
    setAddressError("");
    setApartmentError("");
    showResults([]);
    setLoading(false);
  }, [resetKey]);

  const isDashboard = variant === "dashboard";

  return (
    <div ref={containerRef} className={cn("relative w-full", isDashboard ? "" : "mx-auto max-w-2xl")}>
      <div className={cn("flex justify-center", isDashboard ? "mb-3" : "mb-5")}>
        <div className="segment-pill" role="group" aria-label={t.addressSearch.propertyTypeLabel}>
          <button
            type="button"
            onClick={() => handlePropertyTypeChange("hisa")}
            aria-pressed={propertyType === "hisa"}
            className={`segment-option ${
              propertyType === "hisa" ? "segment-option-active" : "segment-option-inactive"
            }`}
          >
            <Home className="h-4 w-4" />
            <span>{t.addressSearch.house}</span>
          </button>
          <button
            type="button"
            onClick={() => handlePropertyTypeChange("stanovanje")}
            aria-pressed={propertyType === "stanovanje"}
            className={`segment-option ${
              propertyType === "stanovanje" ? "segment-option-active" : "segment-option-inactive"
            }`}
          >
            <Building className="h-4 w-4" />
            <span>{t.addressSearch.apartment}</span>
          </button>
        </div>
      </div>

      <div
        className={cn(
          "rounded-xl border border-border/60 bg-background p-3 sm:p-4",
          !isDashboard && "shadow-sm",
        )}
      >
        <div className="flex flex-col gap-2 md:flex-row">
          <div className="relative w-full flex-1">
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-muted-foreground" />
                <Input
                  ref={inputRef}
                  value={address}
                  onChange={handleAddressChange}
                  onKeyDown={handleInputKeyDown}
                  onFocus={() => dispatch({ type: "open" })}
                  onBlur={dismissSuggestions}
                  placeholder={t.addressSearch.placeholder}
                  aria-label={t.addressSearch.addressLabel}
                  aria-invalid={Boolean(addressError)}
                  aria-describedby={addressError ? addressErrorId : undefined}
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={listOpen}
                  aria-controls={listOpen ? listId : undefined}
                  aria-activedescendant={
                    listOpen && listbox.highlighted >= 0 ? optionId(listbox.highlighted) : undefined
                  }
                  autoComplete="off"
                  enterKeyHint="search"
                  className="pl-11 pr-11 h-14 text-base bg-background border-border/60"
                />
                {loading && (
                  <div className="absolute right-4 top-0 bottom-0 flex items-center" aria-hidden="true">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}
              </div>
              
              <div className={`w-24 transition-all duration-300 ${propertyType === "stanovanje" ? "opacity-100" : "hidden"}`}>
                <Input
                  ref={apartmentRef}
                  value={apartmentNumber}
                  maxLength={20}
                  onChange={(e) => { setApartmentNumber(sanitizeInput(e.target.value).slice(0, 20)); setApartmentError(""); }}
                  aria-invalid={Boolean(apartmentError)}
                  aria-describedby={apartmentError ? apartmentErrorId : undefined}
                  onKeyDown={handleApartmentKeyDown}
                  placeholder={t.common.aptNo}
                  aria-label={t.common.aptNoTitle}
                  enterKeyHint="search"
                  className="h-14 text-base text-center bg-background border-border/60"
                  title={t.common.aptNoTitle}
                />
              </div>
            </div>

            {/* Screen readers hear how many suggestions arrived and how to pick one. */}
            <div className="sr-only" role="status" aria-live="polite">
              {listOpen ? interpolate(t.addressSearch.suggestionsStatus, { count: resultCount }) : ""}
            </div>
            
            {listOpen && (
              <ul
                id={listId}
                role="listbox"
                aria-label={t.addressSearch.suggestionsLabel}
                className="absolute z-50 mt-2 max-h-60 w-full animate-scale-in overflow-y-auto surface-card border-0 bg-background/95 shadow-xl backdrop-blur-md"
              >
                {autocompleteResults.map((result, index) => {
                  const active = index === listbox.highlighted;
                  return (
                    <li
                      key={result.place_id || index}
                      id={optionId(index)}
                      role="option"
                      aria-selected={active}
                      // pointerdown rather than click, so the selection lands
                      // before the input's blur and the outside-click handler.
                      onPointerDown={(e) => {
                        e.preventDefault();
                        selectAddress(result);
                      }}
                      onMouseEnter={() => dispatch({ type: "hover", index })}
                      className={cn(
                        "cursor-pointer border-b border-border/40 px-4 py-3 text-left transition-colors last:border-b-0",
                        active ? "bg-primary/10 ring-1 ring-inset ring-primary/30" : "hover:bg-muted/60",
                      )}
                    >
                      <div className="text-sm font-medium">{result.main_text}</div>
                      {result.secondary_text && (
                        <div className="mt-1 text-xs text-muted-foreground">{result.secondary_text}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          
          <Button
            onClick={handleQuickSearch}
            size="lg"
            disabled={externalLoading}
            className={cn(
              "h-auto min-h-14 w-full px-6 py-3 md:w-auto",
              isDashboard && "bg-primary hover:bg-primary/90",
            )}
          >
            {externalLoading ? (
              <Loader2 className="h-5 w-5 mr-2 shrink-0 animate-spin" />
            ) : (
              <Search className="h-5 w-5 mr-2 shrink-0" />
            )}
            <span className="min-w-0 whitespace-normal">
              {externalLoading ? t.addressSearch.ordering : (submitLabel ?? t.addressSearch.getReport)}
            </span>
            {!externalLoading && <ArrowRight className="h-5 w-5 ml-2 shrink-0" />}
          </Button>
        </div>
        {(addressError || apartmentError) && <div className="mt-3 space-y-1 text-sm text-destructive" role="alert">
          {addressError && <p id={addressErrorId}>{addressError}</p>}
          {apartmentError && <p id={apartmentErrorId}>{apartmentError}</p>}
        </div>}
      </div>
      
      <p className={cn("mt-3 text-center text-sm text-muted-foreground", isDashboard && "text-left")}>
        {propertyType === "hisa" ? t.addressSearch.hintHouse : t.addressSearch.hintApartment}
        {propertyType === "stanovanje" && t.addressSearch.hintApartmentNo}
      </p>
    </div>
  );
}
